/**
 * aiProviderPicker — global helper used by every AI button in the app.
 *
 * On first click of any AI button, this opens a tiny modal asking the
 * user which AI engine to use:
 *   • Atrium AI  (default — uses the Emergent universal LLM key)
 *   • OpenAI     (user supplies their own sk-... key for higher quotas)
 *   • Anthropic  (user supplies an sk-ant-... key)
 *   • Gemini     (user supplies an AIza... key)
 *
 * Choice + key are stored in localStorage under `ai_provider_pref` so
 * subsequent AI calls within the session skip the prompt. A "Change
 * provider" entry in the AI Credentials dialog can clear it.
 *
 * The picker returns a Promise that resolves with the body fragment
 * `{ provider, api_key? }` callers spread into their AI request body.
 * The backend AI endpoints already accept these params and route
 * accordingly — see ai_routes.get_api_key().
 */
import { toast } from 'sonner';

const STORAGE_KEY = 'ai_provider_pref';
const PROVIDERS = [
  { id: 'atrium',    label: 'Atrium AI',  blurb: 'Powered by Project Atrium (gpt-5.2) — included.', requiresKey: false, color: '#1F4FBE' },
  { id: 'openai',    label: 'OpenAI',     blurb: 'Use your own OpenAI API key for higher quotas / pinned models.', requiresKey: true, color: '#10A37F', prefix: 'sk-' },
  { id: 'anthropic', label: 'Anthropic',  blurb: 'Use your own Anthropic (Claude) API key.', requiresKey: true, color: '#D97757', prefix: 'sk-ant-' },
  { id: 'gemini',    label: 'Gemini',     blurb: 'Use your own Google Gemini API key.', requiresKey: true, color: '#4285F4', prefix: 'AIza' },
];

function readPref() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || !obj.provider) return null;
    return obj;
  } catch {
    return null;
  }
}

function writePref(p) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(p)); } catch { /* ignore */ }
}

/** Public — clear the stored preference (used by the AI Credentials dialog). */
export function clearAiProviderPref() {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
}

/**
 * Get a body fragment for an AI request. On first call, prompts the
 * user; on subsequent calls, returns the stored choice.
 *
 * Returns: { provider: 'openai'|'anthropic'|'gemini', api_key?: string }
 *   OR    {} when the user picked Atrium (no override).
 *   OR    null if the user cancelled the picker.
 */
export async function getAiProviderArgs() {
  const cached = readPref();
  if (cached) {
    if (cached.provider === 'atrium') return {};
    return { provider: cached.provider, api_key: cached.api_key || undefined };
  }
  return await openPicker();
}

function openPicker() {
  return new Promise((resolve) => {
    // Build a vanilla DOM modal so this helper works even from
    // non-React contexts (e.g. window.confirm fallbacks).
    const overlay = document.createElement('div');
    overlay.setAttribute('data-testid', 'ai-provider-picker-overlay');
    Object.assign(overlay.style, {
      position: 'fixed', inset: '0', zIndex: '11000',
      background: 'rgba(10,26,47,0.7)', backdropFilter: 'blur(6px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'Manrope, system-ui, sans-serif',
    });
    const card = document.createElement('div');
    Object.assign(card.style, {
      background: 'white', borderRadius: '16px', padding: '24px',
      maxWidth: '480px', width: 'calc(100% - 32px)', maxHeight: '90vh',
      overflow: 'auto', boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
    });
    card.innerHTML = `
      <p style="font-size:11px;font-weight:800;letter-spacing:0.18em;text-transform:uppercase;color:#003DA5;margin:0 0 4px;">Choose your AI</p>
      <p style="font-size:18px;font-weight:800;color:#0A1A2F;margin:0 0 12px;">Which AI platform should we use?</p>
      <p style="font-size:12px;color:#4A5D5D;margin:0 0 18px;">Pick the engine for this and future AI actions. Bring your own key for higher quotas and pinned model versions.</p>
      <div data-role="provider-list" style="display:flex;flex-direction:column;gap:8px;"></div>
      <div style="display:flex;justify-content:flex-end;margin-top:18px;">
        <button data-role="cancel" style="font-size:12px;font-weight:700;padding:8px 14px;border-radius:8px;background:transparent;color:#7A8C8C;border:none;cursor:pointer;">Cancel</button>
      </div>
    `;
    const list = card.querySelector('[data-role="provider-list"]');
    PROVIDERS.forEach((p) => {
      const btn = document.createElement('button');
      btn.setAttribute('data-testid', `ai-provider-${p.id}`);
      Object.assign(btn.style, {
        textAlign: 'left', padding: '12px 14px', borderRadius: '12px',
        background: '#F8F5EC', border: `1px solid ${p.color}33`,
        cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '2px',
        font: 'inherit',
      });
      btn.onmouseenter = () => { btn.style.background = '#FEFBF1'; btn.style.borderColor = p.color; };
      btn.onmouseleave = () => { btn.style.background = '#F8F5EC'; btn.style.borderColor = `${p.color}33`; };
      btn.innerHTML = `
        <span style="font-size:13px;font-weight:800;color:${p.color};">${p.label}</span>
        <span style="font-size:11px;color:#4A5D5D;">${p.blurb}</span>
      `;
      btn.onclick = async () => {
        if (!p.requiresKey) {
          writePref({ provider: 'atrium' });
          cleanup();
          resolve({});
          return;
        }
        const key = window.prompt(`Paste your ${p.label} API key (starts with "${p.prefix}"):`, '');
        if (!key) return;
        if (!key.startsWith(p.prefix.slice(0, 3))) {
          if (!window.confirm(`That doesn't look like a ${p.label} key. Use it anyway?`)) return;
        }
        writePref({ provider: p.id, api_key: key });
        cleanup();
        toast.success(`Saved — future AI calls will use ${p.label}.`);
        resolve({ provider: p.id, api_key: key });
      };
      list.appendChild(btn);
    });
    card.querySelector('[data-role="cancel"]').onclick = () => { cleanup(); resolve(null); };
    overlay.addEventListener('click', (e) => { if (e.target === overlay) { cleanup(); resolve(null); } });
    function cleanup() {
      try { document.body.removeChild(overlay); } catch { /* ignore */ }
    }
    overlay.appendChild(card);
    document.body.appendChild(overlay);
  });
}

/** Public — read the current pref without prompting (for status pills). */
export function readAiProviderPref() { return readPref(); }
